import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Trash2 } from 'lucide-react';
import {
  AgreementStatus,
  PartnerType,
  PoolStatus,
  PortalStatus,
  PositionStage,
  partnerUpdateSchema,
  type PartnerUpdate,
} from '@clawhire/shared';
import { api, isApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { EMPTY, formatDate } from '@/lib/format';
import { useAuth } from '@/auth/AuthProvider';
import { Button, buttonVariants } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonLines } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toaster';
import { EnumBadge } from '@/components/data/EnumBadge';
import { PropertiesPanel, type PropertyField } from '@/components/data/PropertiesPanel';
import { ActivityTimeline } from '@/components/data/ActivityTimeline';
import type { Partner, PartnerDetail } from '@/pages/partners/types';

/** Partner detail (spec §12): properties, positions as intermediary, candidates as supplier, briefings, activity. */
export function PartnerDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { canWrite, isAdmin } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const key = queryKeys.detail('partners', id);

  const detail = useQuery({
    queryKey: key,
    queryFn: () => api.get<PartnerDetail>(`/partners/${encodeURIComponent(id)}`),
    enabled: id.length > 0,
  });
  const partner = detail.data;

  const update = useMutation({
    mutationFn: (patch: PartnerUpdate) =>
      api.patch<Partner>(`/partners/${encodeURIComponent(partner?.id ?? id)}`, patch),
    onSuccess: async (saved) => {
      queryClient.setQueryData<PartnerDetail>(key, (old) => (old ? { ...old, ...saved } : old));
      await queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success('Saved');
    },
  });

  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const remove = useMutation({
    mutationFn: () => api.delete(`/partners/${encodeURIComponent(partner?.id ?? id)}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success(`Partner "${partner?.name ?? ''}" deleted`);
      navigate('/partners', { replace: true });
    },
    onError: (err: Error) => {
      if (isApiError(err) && err.status === 409) setDeleteError(err.message);
      else {
        setDeleteOpen(false);
        toast.error(err.message);
      }
    },
  });

  const fields = React.useMemo<PropertyField[]>(() => {
    if (!partner) return [];
    return [
      { key: 'name', label: 'Name', type: 'text', value: partner.name },
      { key: 'type', label: 'Type', type: 'enum', value: partner.type, def: PartnerType },
      {
        key: 'shortCode',
        label: 'Short code',
        type: 'text',
        value: partner.shortCode,
        help: 'Used in Drive folder and file names. Clear it to re-derive from the name.',
      },
      { key: 'agreement', label: 'Agreement', type: 'enum', value: partner.agreement, def: AgreementStatus },
      { key: 'contactPerson', label: 'Contact person', type: 'text', value: partner.contactPerson },
      { key: 'email', label: 'E-mail', type: 'text', value: partner.email },
      { key: 'phone', label: 'Phone', type: 'text', value: partner.phone },
      { key: 'externalRef', label: 'External ref', type: 'text', value: partner.externalRef },
      { key: 'terms', label: 'Terms', type: 'textarea', value: partner.terms },
      { key: 'notes', label: 'Notes', type: 'textarea', value: partner.notes },
    ];
  }, [partner]);

  if (detail.isLoading) {
    return (
      <>
        <PageHeader title="Partner" breadcrumbs={[{ label: 'Partners', to: '/partners' }, { label: id }]} />
        <SkeletonLines lines={6} />
      </>
    );
  }

  if (detail.isError || !partner) {
    const notFound = isApiError(detail.error) && detail.error.status === 404;
    return (
      <>
        <PageHeader title="Partner" breadcrumbs={[{ label: 'Partners', to: '/partners' }, { label: id }]} />
        <EmptyState
          icon={AlertTriangle}
          title={notFound ? 'Partner not found' : 'The partner could not be loaded'}
          description={notFound ? `No partner matches "${id}".` : detail.error?.message}
          action={
            <Link to="/partners" className={buttonVariants({ variant: 'outline' })}>
              Back to partners
            </Link>
          }
        />
      </>
    );
  }

  const isIntermediary = partner.type === 'INTERMEDIARY';

  return (
    <>
      <PageHeader
        title={partner.name}
        description={[partner.shortCode, partner.contactPerson, partner.email].filter(Boolean).join(' · ')}
        meta={
          <>
            <EnumBadge def={PartnerType} value={partner.type} />
            <EnumBadge def={AgreementStatus} value={partner.agreement} />
          </>
        }
        breadcrumbs={[{ label: 'Partners', to: '/partners' }, { label: partner.name }]}
        actions={
          isAdmin ? (
            <Button
              variant="outline"
              onClick={() => {
                setDeleteError(null);
                setDeleteOpen(true);
              }}
            >
              <Trash2 aria-hidden="true" /> Delete
            </Button>
          ) : null
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="min-w-0 space-y-6">
          <section aria-labelledby="partner-properties" className="rounded-lg border bg-card p-5">
            <h2 id="partner-properties" className="mb-4 text-sm font-semibold">
              Properties
            </h2>
            <PropertiesPanel
              fields={fields}
              readOnly={!canWrite}
              onSave={(patch) => update.mutateAsync(partnerUpdateSchema.parse(patch))}
            />
          </section>

          <Tabs defaultValue={isIntermediary ? 'positions' : 'candidates'}>
            <TabsList className="h-auto flex-wrap justify-start">
              <TabsTrigger value="positions">Positions ({partner.positions.length})</TabsTrigger>
              <TabsTrigger value="candidates">Candidates ({partner.candidates.length})</TabsTrigger>
              <TabsTrigger value="briefings">Briefings ({partner.briefings.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="positions">
              <p className="mb-2 text-xs text-muted-foreground">
                Positions where this partner is the intermediary.
              </p>
              {partner.positions.length === 0 ? (
                <EmptyState
                  compact
                  title="No positions"
                  description="This partner is not the intermediary of any position."
                />
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <caption className="sr-only">Positions as intermediary</caption>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>Request</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Stage</TableHead>
                        <TableHead>Portal</TableHead>
                        <TableHead>Start</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partner.positions.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>
                            <Link
                              to={`/positions/${p.id}`}
                              className="font-medium underline-offset-4 hover:underline"
                            >
                              {p.requestNo}
                            </Link>
                          </TableCell>
                          <TableCell>{p.role}</TableCell>
                          <TableCell>
                            <EnumBadge def={PositionStage} value={p.stage} size="sm" />
                          </TableCell>
                          <TableCell>
                            <EnumBadge def={PortalStatus} value={p.portalStatus} size="sm" />
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {p.startDate ? formatDate(p.startDate) : EMPTY}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="candidates">
              <p className="mb-2 text-xs text-muted-foreground">Candidates supplied by this partner.</p>
              {partner.candidates.length === 0 ? (
                <EmptyState
                  compact
                  title="No candidates"
                  description="No candidate lists this partner as supplier."
                />
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <caption className="sr-only">Candidates as supplier</caption>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>ID</TableHead>
                        <TableHead>Name / label</TableHead>
                        <TableHead>Pool status</TableHead>
                        <TableHead>Added</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partner.candidates.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell>
                            <Link
                              to={`/candidates/${c.id}`}
                              className="font-medium underline-offset-4 hover:underline"
                            >
                              {c.candidateId}
                            </Link>
                          </TableCell>
                          <TableCell>
                            {c.fullName ?? c.label ?? <span className="text-muted-foreground">{EMPTY}</span>}
                          </TableCell>
                          <TableCell>
                            <EnumBadge def={PoolStatus} value={c.poolStatus} size="sm" />
                          </TableCell>
                          <TableCell className="tabular-nums">{formatDate(c.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="briefings">
              <p className="mb-2 text-xs text-muted-foreground">Job descriptions sent to this partner.</p>
              {partner.briefings.length === 0 ? (
                <EmptyState
                  compact
                  title="No briefings"
                  description="No JD has been sent to this partner yet."
                />
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <caption className="sr-only">JD briefings</caption>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>Position</TableHead>
                        <TableHead>JD version</TableHead>
                        <TableHead>Sent on</TableHead>
                        <TableHead>Stop sourcing</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partner.briefings.map((b) => (
                        <TableRow key={b.id}>
                          <TableCell>
                            <Link
                              to={`/positions/${b.position.id}`}
                              className="font-medium underline-offset-4 hover:underline"
                            >
                              {b.position.requestNo} {b.position.role}
                            </Link>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {b.jdVersion ? (
                              <>
                                {b.jdVersion.reference} v{String(b.jdVersion.version).padStart(2, '0')}{' '}
                                {b.jdVersion.lang}
                              </>
                            ) : (
                              <span className="text-muted-foreground">{EMPTY}</span>
                            )}
                          </TableCell>
                          <TableCell className="tabular-nums">{formatDate(b.sentOn)}</TableCell>
                          <TableCell className="tabular-nums">
                            {b.stopSourcingSentOn ? (
                              formatDate(b.stopSourcingSentOn)
                            ) : (
                              <span className="text-muted-foreground">{EMPTY}</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[16rem] truncate" title={b.notes ?? undefined}>
                            {b.notes ?? <span className="text-muted-foreground">{EMPTY}</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <aside className="min-w-0 space-y-6">
          <section className="rounded-lg border bg-card p-5">
            <h2 className="mb-4 text-sm font-semibold">Activity</h2>
            <ActivityTimeline
              entityType="PARTNER"
              entityId={partner.id}
              entries={partner.activity}
              canAdd={canWrite}
              onChanged={() => void queryClient.invalidateQueries({ queryKey: key })}
            />
          </section>
          <dl className="grid grid-cols-2 gap-2 rounded-lg border bg-card p-5 text-xs text-muted-foreground">
            <dt>Created</dt>
            <dd className="tabular-nums">
              {formatDate(partner.createdAt)}
              {partner.createdBy ? ` · ${partner.createdBy}` : ''}
            </dd>
            <dt>Updated</dt>
            <dd className="tabular-nums">
              {formatDate(partner.updatedAt)}
              {partner.updatedBy ? ` · ${partner.updatedBy}` : ''}
            </dd>
          </dl>
        </aside>
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {partner.name}?</DialogTitle>
            <DialogDescription>
              The partner is removed from lists and lookups (soft delete). Partners linked to positions or
              candidates cannot be deleted.
            </DialogDescription>
          </DialogHeader>
          {deleteError ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            >
              {deleteError}
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
              disabled={Boolean(deleteError)}
            >
              Delete partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
